/**
 * Utility for waiting for hitsy-container.js to load and modifying texts
 */

// Flag to prevent recursion when updating texts
let bfIsUpdatingTexts = false;

/**
 * Waits for hitsy-container.js to load
 * @param {number} timeout - Maximum wait time in ms (default 10000)
 * @returns {Promise<boolean>} - true if script is loaded, false if timeout
 */
function waitForBfHitsyLoad(timeout = 10000) {
  return new Promise((resolve) => {
    // Check if script is already loaded
    if (
      window.hitsyProgress &&
      window.HitsyProgress &&
      customElements.get("hitsy-container")
    ) {
      resolve(true);
      return;
    }

    const startTime = Date.now();
    const checkInterval = 100; // Check every 100ms

    const intervalId = setInterval(() => {
      const isLoaded =
        window.hitsyProgress &&
        window.HitsyProgress &&
        customElements.get("hitsy-container");

      if (isLoaded) {
        clearInterval(intervalId);
        resolve(true);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(intervalId);
        resolve(false);
      }
    }, checkInterval);
  });
}

/**
 * Waits for elements to be defined via customElements.whenDefined
 * @returns {Promise<void>}
 */
function waitForBfHitsyElements() {
  return Promise.all([
    customElements.whenDefined("hitsy-container"),
    customElements.whenDefined("hitsy-text"),
    customElements.whenDefined("hitsy-card"),
  ]);
}

/**
 * Modifies texts in hitsy configuration
 * @param {number} configIndex - Configuration index
 * @param {string} textChanges.beforeText - New "before achieving" text (supports {amount} placeholder)
 * @param {string} textChanges.afterText - New "after achieving" text
 * @param {number} tierIndex - Tier index (optional, if you need to modify a specific tier)
 */
function modifyBfHitsyTexts(configIndex, textChanges, tierIndex = null) {
  if (!window.hitsyProgress || !window.hitsyProgress[configIndex]) {
    console.warn("BfHitsy config not found for index:", configIndex);
    return;
  }

  const config = window.hitsyProgress[configIndex];
  const rewardBasis = config.rewardBasis;
  const tiers =
    rewardBasis === "cart-total"
      ? config.tiersCartTotal
      : config.tiersNumberOfItems;

  if (tierIndex !== null) {
    // Modify specific tier // TODO: Her could be an Error textChanges has different structure
    if (tiers[tierIndex]) {
      if (textChanges.beforeText !== undefined) {
        tiers[tierIndex].textBeforeAchieving = textChanges.beforeText;
      }
      if (textChanges.afterText !== undefined) {
        tiers[tierIndex].textAfterAchieving = textChanges.afterText;
      }
    }
  } else {
    // Modify all tiers
    tiers.forEach((tier) => {
      const {rewardType} = tier;
      if (!rewardType) return;

      const updatedText = textChanges[`${rewardType}`];

      if (updatedText.beforeText !== undefined) {
        tier.textBeforeAchieving = updatedText.beforeText;
      }

      if (updatedText.afterText !== undefined) {
        tier.textAfterAchieving = updatedText.afterText;
      }
    });
  }

  // Do NOT call updateExistingBfHitsyTexts here to avoid conflicts
  // HitsyCard will update texts automatically on next Hitsy:cartUpdated event
  // If you need to update immediately, use updateExistingHitsyTexts separately
}

/**
 * Updates texts in already rendered elements
 * @param {number} configIndex - Configuration index
 * @param {boolean} force - Force update (ignores protection flag)
 */
function updateExistingBfHitsyTexts(configIndex, force = false) {
  // Protection against recursion
  if (bfIsUpdatingTexts && !force) {
    return;
  }

  bfIsUpdatingTexts = true;

  try {
    // IMPORTANT: Do not update elements directly, as HitsyCard does this itself
    // Instead, just ensure the configuration is changed
    // HitsyCard.updateTexts() will use the updated configuration on the next event

    // If immediate update is needed, update hitsy-card elements directly
    const hitsyCards = document.querySelectorAll("hitsy-card");
    hitsyCards.forEach((card) => {
      const cardConfigIndex = card.getAttribute("configIndex");
      if (cardConfigIndex === configIndex.toString() && card.updateTexts) {
        // Call updateTexts method directly if available
        try {
          card.updateTexts();
        } catch (e) {
          // If method is not available, update elements manually
          const beforeText = card.querySelector('hitsy-text[textType="before"]');
          const afterText = card.querySelector('hitsy-text[textType="after"]');

          if (beforeText || afterText) {
            const [beforeTextValue, afterTextValue] = createTexts(configIndex);
            if (beforeText) {
              updateTextElement(beforeText, beforeTextValue);
            }
            if (afterText) {
              updateTextElement(afterText, afterTextValue);
            }
          }
        }
      }
    });

    // Also update elements inside shadow DOM hitsy-container
    const hitsyContainers = document.querySelectorAll("hitsy-container");
    hitsyContainers.forEach((container) => {
      const containerConfigIndex = container.getAttribute("configIndex");
      if (containerConfigIndex === configIndex.toString()) {
        const shadowRoot = container.shadowRoot;
        if (shadowRoot) {
          const hitsyCard = shadowRoot.querySelector("hitsy-card");
          if (hitsyCard && hitsyCard.updateTexts) {
            try {
              hitsyCard.updateTexts();
            } catch (e) {
              // Fallback: update elements directly
              const beforeText = shadowRoot.querySelector('hitsy-text[textType="before"]');
              const afterText = shadowRoot.querySelector('hitsy-text[textType="after"]');

              if (beforeText || afterText) {
                const [beforeTextValue, afterTextValue] = createTexts(configIndex);
                if (beforeText) {
                  updateTextElement(beforeText, beforeTextValue);
                }
                if (afterText) {
                  updateTextElement(afterText, afterTextValue);
                }
              }
            }
          }
        }
      }
    });
  } finally {
    // Reset flag with small delay to avoid repeated calls
    setTimeout(() => {
      bfIsUpdatingTexts = false;
    }, 100);
  }
}

/**
 * Applies project font family to hitsy-text element
 * @param {HTMLElement} element - hitsy-text element
 */
function applyProjectFontFamily(element) {
  if (!element || !element.textView) return;

  // Get font family from CSS variables
  const bodyFontFamily = getComputedStyle(document.documentElement)
    .getPropertyValue('--global-font-body-family')
    .trim();

  if (bodyFontFamily) {
    element.textView.style.fontFamily = bodyFontFamily;
  }
}

/**
 * Updates text element with proper visibility handling
 * @param {HTMLElement} element - hitsy-text element
 * @param {string} textValue - Text value to set
 */
function updateTextElement(element, textValue) {
  if (!element) return;

  if (textValue && textValue.trim() !== "") {
    element.setAttribute("text", textValue);
    element.style.display = "block";
    applyProjectFontFamily(element);
  } else {
    element.setAttribute("text", "");
    element.style.display = "none";
  }
}

/**
 * Uses original createTexts function from hitsy-container.js
 * If unavailable, creates its own version
 *
 * IMPORTANT: This function should use the same logic as the original createTexts
 * from hitsy-container.js to avoid discrepancies
 */
function createTexts(configIndex) {
  // Try to use original function via eval (if it's in global scope)
  // Or use our version that copies the original logic
  const config = window.hitsyProgress[configIndex];
  if (!config) return ["", ""];

  const rewardBasis = config.rewardBasis;
  const tiers =
    rewardBasis === "cart-total"
      ? config.tiersCartTotal
      : config.tiersNumberOfItems;

  const amount = getAmount(config);
  const currentTierIndex = tiers
    .map((tier) => tier.amount)
    .reduceRight((foundIndex, amountValue, index) => {
      return foundIndex === -1 && amountValue <= amount ? index : foundIndex;
    }, -1);
  const isLastTier = currentTierIndex === tiers.length - 1;

  const nextTier =
    currentTierIndex === -1
      ? tiers[0]
      : isLastTier
        ? null
        : tiers[currentTierIndex + 1];
  const previousTier = currentTierIndex > -1 ? tiers[currentTierIndex] : null;

  // Logic for afterText:
  // - If currentTierIndex === -1 (nothing achieved): no afterText
  // - If currentTierIndex >= 0 (at least one tier achieved): show afterText of the LAST achieved tier only
  //   This matches the original behavior from hitsy-container.js
  const afterText = previousTier?.textAfterAchieving || "";

  // Logic for beforeText:
  // - If no nextTier (all tiers achieved): no beforeText (will be hidden)
  // - Otherwise: show beforeText of next tier
  if (!nextTier) return ["", afterText];

  const leftAmount = nextTier.amount - amount;
  const formattedAmount =
    rewardBasis === "cart-total"
      ? bfHitsyFormatMoney(leftAmount)
      : leftAmount.toString();
  const beforeText = nextTier.textBeforeAchieving.replace(
    "{amount}",
    formattedAmount,
  );

  return [beforeText, afterText];
}

/**
 * Helper function to get amount (copy from hitsy-container.js)
 */
function getAmount(config) {
  const rewardBasis = config.rewardBasis;
  const cart = window.hitsyCart;

  if (rewardBasis === "cart-total") {
    const amount =
      config.discountSetup === "auto"
        ? cart.original_total_price
        : cart.items_subtotal_price;
    return amount / 100;
  } else {
    return cart.item_count;
  }
}

/**
 * Helper function to format money (copy from hitsy-container.js)
 */
function bfHitsyFormatMoney(amount) {
  const moneyFormat = window.hitsyPriceFormat;

  const formatPattern = /{{\s*(\w+)\s*}}/;
  const formatType = moneyFormat.match(formatPattern)?.[1] || "amount";

  const formatWithDelimiters = (
    num,
    decimalPlaces = 2,
    thousandsSeparator = ",",
    decimalSeparator = ".",
  ) => {
    if (typeof num === "string") num = Number(num);
    if (isNaN(num) || num == null) return "0";

    num = num.toFixed(decimalPlaces);
    const [integerPart, decimalPart] = num.split(".");

    return (
      integerPart.replace(
        /(\d)(?=(\d{3})+(?!\d))/g,
        `$1${thousandsSeparator}`,
      ) + (decimalPart ? `${decimalSeparator}${decimalPart}` : "")
    );
  };

  const formatMapping = {
    amount: formatWithDelimiters(amount, 2),
    amount_no_decimals: formatWithDelimiters(amount, 0),
    amount_no_decimals_with_space_separator: formatWithDelimiters(
      amount,
      0,
      " ",
    ),
    amount_with_comma_separator: formatWithDelimiters(amount, 2, ".", ","),
    amount_no_decimals_with_comma_separator: formatWithDelimiters(
      amount,
      0,
      ".",
    ),
    amount_with_apostrophe_separator: formatWithDelimiters(amount, 2, "'", "."),
    amount_with_space_separator: formatWithDelimiters(amount, 2, " ", ","),
    amount_with_period_and_space_separator: formatWithDelimiters(
      amount,
      2,
      " ",
      ".",
    ),
  };

  return moneyFormat.replace(
    formatPattern,
    formatMapping[formatType] || formatMapping.amount,
  );
}

/**
 * Main function to initialize text modification
 * @param {Object} options - Options
 * @param {number} options.configIndex - Configuration index (default 0)
 * @param {Object} options.textChanges - Text changes
 * @param {number} options.tierIndex - Tier index (optional)
 * @param {number} options.timeout - Wait timeout in ms
 */
async function initBfHitsyTextModification(options = {}) {
  const {
    configIndex = 0,
    textChanges = {},
    tierIndex = null,
    timeout = 10000,
  } = options;

  // Wait for script to load
  const isLoaded = await waitForBfHitsyLoad(timeout);
  if (!isLoaded) {
    console.error("BfHitsy script not loaded within timeout");
    return false;
  }

  // Additionally wait for custom elements to be defined
  try {
    await waitForBfHitsyElements();
  } catch (error) {
    console.warn("Some hitsy elements not defined:", error);
  }

  // Modify texts in configuration
  modifyBfHitsyTexts(configIndex, textChanges, tierIndex);

  // Do NOT add additional listener for Hitsy:cartUpdated event
  // HitsyCard already has its own listener that will update texts automatically
  // on next Hitsy:cartUpdated event

  // Update texts once after configuration change
  // Use setTimeout for delayed update to avoid conflicts
  // and give HitsyCard time to initialize
  setTimeout(() => {
    if (!bfIsUpdatingTexts) {
      updateExistingBfHitsyTexts(configIndex, true);
    }
  }, 200);

  return true;
}

// Export for use
if (typeof window !== "undefined") {
  window.BfHitsyTextModifier = {
    waitForBfHitsyLoad,
    waitForBfHitsyElements,
    modifyBfHitsyTexts,
    updateExistingBfHitsyTexts,
    initBfHitsyTextModification,
  };
}

// Call Init
(function() {
  'use strict';

  if (!window.location.href.includes('fr')) return;

  const jsonData = JSON.parse(document.querySelector("[data-histy-progress-bar-json]").textContent);
  // Wait for DOM and Hitsy script to load
  function initBfHitsyTexts() {
    // Check if utility is available
    if (!window.BfHitsyTextModifier) {
      console.warn('BfHitsyTextModifier is not loaded. Make sure hitsy-text-modifier.js is included.');
      return;
    }

    // Example texts (commented out):
    // beforeText: "Plus que <b>{amount}</b> dans ton panier pour recevoir un <b>cadeau surprise</b>",
    // afterText: "Tu as la <b>livraison offerte</b>"

    // Modify texts
    // NOTE: The logic automatically handles text display based on currentTierIndex:
    // - When currentTierIndex === -1 (nothing achieved): only beforeText of first tier is shown
    // - When currentTierIndex === 0 (first tier achieved): beforeText of second tier + afterText of first tier are shown
    // - When currentTierIndex === last (all tiers achieved): only afterText (combined) is shown, beforeText is hidden

    const textChanges = {
      'free-shipping': {
        // "Before achieving" text - {amount} will be replaced with sum/quantity
        // This text will be shown for the NEXT tier to achieve
        beforeText: jsonData.free_shipping_with_amount,

        // "After achieving" text
        // This text will be shown when the current tier is achieved
        // If all tiers are achieved, all afterTexts will be combined
        afterText: jsonData.free_shipping
      },
      'free-gift': {
        beforeText: jsonData.free_gift_with_amount,
        afterText: jsonData.free_gift
      }
    }
    window.BfHitsyTextModifier.initBfHitsyTextModification({
      configIndex: 0, // Usually 0, but can be different index
      textChanges
    }).then(success => {
      if (!success) {
        console.debug('⚠️ ⚠️ ⚠️ BF - Failed to modify Hitsy texts (timeout)');
      }
    });
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBfHitsyTexts);
  } else {
    // DOM already loaded
    initBfHitsyTexts();
  }
})();

// Usage examples:
/*
// Option 1: Using async/await
(async () => {
  await window.BfHitsyTextModifier.initBfHitsyTextModification({
    configIndex: 0,
    textChanges: {
      beforeText: "Add {amount} more for free shipping",
      afterText: "Congratulations! You got free shipping!"
    }
  });
})();

// Option 2: Modifying specific tier
window.BfHitsyTextModifier.initBfHitsyTextModification({
  configIndex: 0,
  tierIndex: 0, // First tier
  textChanges: {
    beforeText: "Add {amount} more for free shipping",
    afterText: "Free shipping activated!"
  }
});

// Option 3: Manual wait and modify
window.BfHitsyTextModifier.waitForBfHitsyLoad().then((loaded) => {
  if (loaded) {
    window.BfHitsyTextModifier.modifyBfHitsyTexts(0, {
      beforeText: "New text before: {amount}",
      afterText: "New text after"
    });
  }
});
*/
